import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Avatar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { FormatQuote } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

const Reviews = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [currentReview, setCurrentReview] = useState(0);
  const [progress, setProgress] = useState(0);

  const reviews = [
    {
      id: 1,
      name: "Yash",
      avatar:
        "https://a0.muscache.com/im/pictures/user/User/original/14e547d0-3b39-479a-8160-27c4c72d2fe5.jpeg?im_w=240",
      rating: 5,
      title: "Lovely and peaceful getaway",
      text: "Our stay was truly wonderful. The villa was charming, cozy, and spotlessly clean. The peaceful surroundings and beautiful scenery made it a perfect retreat. Every detail was thoughtfully arranged for comfort. The host was attentive and helpful. We left feeling refreshed, grateful, and eager to return for another memorable experience soon.",
    },
    {
      id: 2,
      name: "Pramod",
      avatar:
        "https://a0.muscache.com/im/pictures/user/f705eed1-5875-4ba9-b926-be8ee821a490.jpg?im_w=240",
      rating: 4,
      title: "Fantastic for groups, kitchen could improve",
      text: "A fantastic spot for groups of friends and family to spend quality time together. Spacious rooms and living areas, clean swimming pool, and a terrace perfect for small parties. The property looked exactly as shown in the photos. We enjoyed our stay and would recommend it to others seeking comfort.",
    },
    {
      id: 3,
      name: "Subramanian",
      avatar:
        "https://a0.muscache.com/im/pictures/user/14a1ffb1-11b2-4706-9dce-321dbf26a145.jpg?im_w=240",
      rating: 4,
      title: "Fantastic stay & clean pool",
      text: "Fantastic stay, house looked just like the pictures. The pool was clean, and the helper was friendly and assistive. The location is also decent, close enough to the main road. We had a relaxing time and would recommend this place to anyone looking for a peaceful getaway in Goa.",
    },
    {
      id: 4,
      avatar:
        "https://a0.muscache.com/im/users/9210780/profile_pic/1380824991/original.jpg?im_w=240",
      name: "Prakash",
      rating: 4,
      title: "Peaceful and relaxing",
      text: "A peaceful and relaxing stay inside a gated apartment complex close to the beach. The rooms were comfortable and clean, and the environment was quiet. We enjoyed the easy access to the beach and the friendly atmosphere. Would recommend this property to anyone seeking a calm vacation.",
    },
    {
      id: 5,
      name: "Aayush",
      avatar:
        "https://a0.muscache.com/im/pictures/user/User-370554393/original/8824b2e4-77a0-4012-ae5f-2237c36c5449.jpeg?im_w=240",
      rating: 4,
      title: "Perfect getaway",
      text: "Lovely stay! The villa was peaceful, clean, and well-equipped. We enjoyed spending time with friends, relaxing in the comfortable rooms, and exploring the nearby attractions. The host was helpful and responsive. Would definitely recommend this place for a perfect, peaceful getaway in Goa.",
    },
  ];

  const TRANSITION_DURATION = 5000; // 5 seconds per testimonial

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          setCurrentReview((prev) => (prev + 1) % reviews.length);
          return 0;
        }
        return prevProgress + 100 / (TRANSITION_DURATION / 100);
      });
    }, 100);

    return () => clearInterval(interval);
  }, [reviews.length]);

  const currentReviewData = reviews[currentReview];

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 8, sm: 10, md: 12 },
        backgroundColor: "#f2f3f5",
        position: "relative",
        overflow: "hidden",
        borderRadius: 4,
        mb: 8,
        width: "calc(100% - 32px)", // Adjust width to fit within container
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Quote Icon */}
          <Box
            data-aos="fade-up"
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              mb: 4,
            }}
          >
            <FormatQuote
              sx={{
                fontSize: { xs: "3rem", sm: "4rem", md: "5rem" },
                color: theme.palette.text.primary,
                transform: "rotate(180deg)",
              }}
            />
          </Box>

          {/* Animated Review Text */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentReview}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              <Typography
                variant="body1"
                data-aos="fade-up"
                data-aos-delay="200"
                sx={{
                  fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                  lineHeight: 1.6,
                  color: theme.palette.text.primary,
                  fontWeight: 400,
                  mb: 6,
                  textAlign: "left",
                  maxWidth: "100%",
                  minHeight: { xs: "120px", sm: "140px", md: "160px" },
                }}
              >
                {currentReviewData.text}
              </Typography>
            </motion.div>
          </AnimatePresence>

          {/* Author Section with Animated Avatar */}
          <Box
            data-aos="fade-up"
            data-aos-delay="400"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 3,
            }}
          >
            {/* Avatar with Progress Ring */}
            <Box sx={{ position: "relative" }}>
              {/* Progress Ring Background */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: 72, sm: 92 },
                  height: { xs: 72, sm: 92 },
                  borderRadius: "50%",
                  border: `3px solid ${theme.palette.grey[300]}`,
                  zIndex: 1,
                }}
              />

              {/* Animated Progress Ring */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: 80, sm: 98 },
                  height: { xs: 80, sm: 98 },
                  borderRadius: "50%",
                  zIndex: 99,
                }}
              >
                <svg
                  width="100%"
                  height="100%"
                  style={{ transform: "rotate(-90deg)" }}
                  viewBox="0 0 86 86"
                >
                  <circle
                    cx="43"
                    cy="43"
                    r="40"
                    fill="none"
                    stroke={theme.palette.primary.main}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${
                      2 * Math.PI * 40 * (1 - progress / 100)
                    }`}
                    style={{
                      transition: "stroke-dashoffset 0.1s ease",
                    }}
                  />
                </svg>
              </Box>

              {/* Avatar */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentReview}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Avatar
                    src={currentReviewData.avatar}
                    alt={currentReviewData.author}
                    sx={{
                      width: { xs: 60, sm: 70, md: 76 },
                      height: { xs: 60, sm: 70, md: 76 },
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      zIndex: 3,
                      position: "relative",
                    }}
                  />
                </motion.div>
              </AnimatePresence>
            </Box>

            {/* Animated Author Name */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentReview}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontFamily: "Monument-Private",
                    fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  {currentReviewData.name}
                </Typography>
              </motion.div>
            </AnimatePresence>
          </Box>

          {/* Progress Indicators */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              mt: 4,
            }}
          >
            {reviews.map((_, index) => (
              <Box
                key={index}
                onClick={() => {
                  setCurrentReview(index);
                  setProgress(0);
                }}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor:
                    index === currentReview
                      ? "#000000"
                      : theme.palette.grey[300],
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.2)",
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Container>
  );
};

export default Reviews;
